import { PROGRAM_PHASES, TERMINAL_WORK_ITEM_STATUSES, type ProgramPhase, type ProgramSettings, type WorkItemDetail } from '@deskworks/shared-types';
import { CheckCircle2, Circle, CircleDot, Pause, Rocket, Settings2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { formatUsd } from '@/lib/usage';
import { cn } from '@/lib/utils';

export function phaseLabel(phase: ProgramPhase): string {
  return {
    clarify: t('Clarify the task'),
    vision: t('Target picture'),
    roadmap: t('Roadmap'),
    build: t('Build'),
    prove: t('Prove it works'),
    handover: t('Hand over'),
    done: t('Done'),
  }[phase];
}

function stopText(reason: string): string {
  return reason === 'budget_day'
    ? t('Paused for today: the day’s budget is used up. It goes on tomorrow, or now when you raise it.')
    : reason === 'budget_total'
      ? t('Stopped: the budget is used up. Raise it to go on.')
      : t('Stopped: its end has come. Set a later end to go on.');
}

/**
 * A program (docs/features/programs.md): one of VICKI's tasks she drives for days — its phase, what it cost, its target
 * picture and decisions, and its settings. On one of her tasks that is none yet: make it one.
 */
export function ProgramPanel({ item }: { item: WorkItemDetail }) {
  const p = item.program;
  if (!p) {
    if (!item.frontDesk || item.kind !== 'task' || TERMINAL_WORK_ITEM_STATUSES.includes(item.status)) return null;
    return (
      <Section icon={<Rocket />} title={t('Program')} description={t('For a big goal over days: VICKI clarifies it, draws the target picture, plans and builds it with the desks, with a daily report.')}>
        <ProgramSettingsDialog item={item}>
          <Button size="sm" variant="outline">
            <Rocket /> {t('Run as a program')}
          </Button>
        </ProgramSettingsDialog>
      </Section>
    );
  }
  const at = PROGRAM_PHASES.indexOf(p.phase);
  return (
    <Section
      icon={<Rocket />}
      title={t('Program')}
      description={phaseLabel(p.phase)}
      actions={
        <ProgramSettingsDialog item={item}>
          <Button variant="ghost" size="xs" title={t('Settings')}>
            <Settings2 /> {t('Settings')}
          </Button>
        </ProgramSettingsDialog>
      }
    >
      <div className="grid gap-4">
        {p.stoppedReason && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-950 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
            <Pause className="mt-0.5 size-3.5 shrink-0" /> {stopText(p.stoppedReason)}
          </div>
        )}
        <ol className="flex flex-col gap-1.5">
          {PROGRAM_PHASES.filter((ph) => ph !== 'done').map((ph, i) => (
            <li key={ph} className={cn('flex items-center gap-2 text-[13px]', i < at || p.phase === 'done' ? 'text-muted-foreground' : i > at && 'text-muted-foreground/70')}>
              {i < at || p.phase === 'done' ? (
                <CheckCircle2 className="size-4 shrink-0 text-emerald-500" />
              ) : i === at ? (
                <CircleDot className="size-4 shrink-0 text-indigo-500" />
              ) : (
                <Circle className="size-4 shrink-0" />
              )}
              <span className={cn(i === at && 'font-medium text-foreground')}>{phaseLabel(ph)}</span>
            </li>
          ))}
        </ol>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[13px]">
          <Row label={t('Spent today')}>
            {formatUsd(p.spend.todayUsd)}
            {p.budgetDayUsd ? <span className="text-muted-foreground"> {t('of {budget}', { budget: formatUsd(p.budgetDayUsd) })}</span> : null}
          </Row>
          <Row label={t('Spent in all')}>
            {formatUsd(p.spend.totalUsd)}
            {p.budgetTotalUsd ? <span className="text-muted-foreground"> {t('of {budget}', { budget: formatUsd(p.budgetTotalUsd) })}</span> : <span className="text-muted-foreground"> · {t('no limit')}</span>}
          </Row>
          <Row label={t('Daily report')}>{t('at {hour}:00', { hour: p.reportHour })}{p.reportPerWave ? ` · ${t('and after each wave')}` : ''}</Row>
          <Row label={t('Rights')}>
            {p.effectiveRights === 'read_write' ? t('Read/Write') : t('Read')}
            <span className="text-muted-foreground"> · {p.rights ? t('for this program') : t('the account’s')}</span>
          </Row>
          {p.endsAt && (
            <Row label={t('Ends')}>
              <RelativeTime value={p.endsAt} />
            </Row>
          )}
        </dl>
        {p.vision?.trim() && <Fold title={t('Target picture')} text={p.vision} />}
        {p.decisions.length > 0 && (
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">{t('Decisions ({n})', { n: p.decisions.length })}</div>
            <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto text-[13px]">
              {[...p.decisions].reverse().map((d, i) => (
                <li key={i} className="rounded-md bg-muted/50 px-2.5 py-1.5">
                  <span className="text-xs text-muted-foreground">
                    {d.by === 'person' ? t('People') : 'VICKI'} · <RelativeTime value={d.at} />
                  </span>
                  <div className="break-words">{d.text}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function Fold({ title, text }: { title: string; text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="mb-1 cursor-pointer text-xs font-medium text-muted-foreground hover:text-foreground">
        {title} · {open ? t('Hide') : t('Show')}
      </button>
      {open && (
        <div className="max-h-96 overflow-y-auto rounded-md border px-3 py-2 text-[13px]">
          <Markdown text={text} />
        </div>
      )}
    </div>
  );
}

/** "" ⇄ null for the optional numbers. */
function money(v: string): number | null {
  const n = Number(v.replace(',', '.'));
  return v.trim() && Number.isFinite(n) && n > 0 ? n : null;
}

function localInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

/** Everything about a program a person sets; on a task that is none yet, saving makes it one. */
function ProgramSettingsDialog({ item, children }: { item: WorkItemDetail; children: ReactNode }) {
  const p = item.program;
  const [open, setOpen] = useState(false);
  const [rights, setRights] = useState<string>(p?.rights ?? 'account');
  const [gateVision, setGateVision] = useState(p?.gateVision ?? true);
  const [day, setDay] = useState(p?.budgetDayUsd ? String(p.budgetDayUsd) : '');
  const [total, setTotal] = useState(p?.budgetTotalUsd ? String(p.budgetTotalUsd) : '');
  const [ends, setEnds] = useState(localInput(p?.endsAt ?? null));
  const [hour, setHour] = useState(String(p?.reportHour ?? 18));
  const [perWave, setPerWave] = useState(p?.reportPerWave ?? false);
  const save = useApiMutation(
    () => {
      const body: Partial<ProgramSettings> = {
        rights: rights === 'account' ? null : (rights as ProgramSettings['rights']),
        gateVision,
        budgetDayUsd: money(day),
        budgetTotalUsd: money(total),
        endsAt: ends ? new Date(ends).toISOString() : null,
        reportHour: Number(hour),
        reportPerWave: perWave,
      };
      return api.workItems.saveProgram(item.id, body);
    },
    {
      invalidate: [qk.workItem(item.id)],
      success: p ? t('Program settings saved') : t('VICKI runs it as a program now'),
      onSuccess: () => setOpen(false),
    },
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{p ? t('Program settings') : t('Run as a program')}</DialogTitle>
          <DialogDescription>{t('How freely VICKI works on it, what it may cost and when she reports. Everything can be changed later.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label>{t('Rights')}</Label>
            <Select value={rights} onValueChange={setRights}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="account">{t('As set for VICKI on the account')}</SelectItem>
                <SelectItem value="read">{t('Read: she prepares, people approve every change')}</SelectItem>
                <SelectItem value="read_write">{t('Read/Write: she builds herself')}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('Money, contracts, talking to customers, production and credentials always come to you.')}</p>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>{t('Target picture waits for your go-ahead')}</span>
            <Switch checked={gateVision} onCheckedChange={setGateVision} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="budget-day">{t('Budget per day (USD)')}</Label>
              <Input id="budget-day" inputMode="decimal" value={day} onChange={(e) => setDay(e.target.value)} placeholder={t('unlimited')} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="budget-total">{t('Budget in all (USD)')}</Label>
              <Input id="budget-total" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} placeholder={t('unlimited')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ends">{t('Ends')}</Label>
              <Input id="ends" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>{t('Daily report at')}</Label>
              <Select value={hour} onValueChange={setHour}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, h) => (
                    <SelectItem key={h} value={String(h)}>{`${String(h).padStart(2, '0')}:00`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>{t('A short report after each wave too')}</span>
            <Switch checked={perWave} onCheckedChange={setPerWave} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            {p ? t('Save') : t('Run as a program')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
